import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { AuthProvider } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { BrandingProvider } from './contexts/BrandingContext';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionProtectedRoute from './components/PermissionProtectedRoute';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PlatformAdminPage from './pages/platform/PlatformAdminPage';
import LocationsPage from './pages/locations/LocationsPage';
import CreateLocationPage from './pages/locations/CreateLocationPage';
import EditLocationPage from './pages/locations/EditLocationPage';
import ViewLocationPage from './pages/locations/ViewLocationPage';
import CamerasPage from './pages/cameras/CamerasPage';
import ViewCameraPage from './pages/cameras/ViewCameraPage';
import EditCameraPage from './pages/cameras/EditCameraPage';
import EventsPage from './pages/events/EventsPage';
import ViewEventPage from './pages/events/ViewEventPage';
import EditEventPage from './pages/events/EditEventPage';
import AISearchPage from './pages/events/AISearchPage';
import ActionsPage from './pages/actions/ActionsPage';
import SensorsDevicesPage from './pages/devices/SensorsDevicesPage';
import ViewActionPage from './pages/actions/ViewActionPage';
import EditActionPage from './pages/actions/EditActionPage';
import VisitorLogsPage from './pages/visitor-logs/VisitorLogsPage';
import { ViewEntryExitPage, CreateEntryExitPage, EditEntryExitPage } from './pages/visitor-logs';
import { ParkingLotsPage } from './pages/parking';
import ParkingSpotsPage from './pages/parking/ParkingSpotsPage';
import { TenantsPage, CreateTenantPage, EditTenantPage, ViewTenantPage, CompanyProfilePage, CompanyEditPage } from './pages/tenants';
import { PersonsPage, CreateEditPersonPage, ViewPersonPage } from './pages/persons';
import LiveViewPage from './pages/live/LiveViewPage';
import { IntegrationsPage } from './pages/integrations';
import { ConfigurationPage } from './pages/configuration';
import { ProfilePage } from './pages/profile';
import { NotFoundPage } from './pages/errors';
import './App.css';
import EducationDemoPage from './pages/education/EducationDemoPage';

function App() {
  // Override console.error and console.log to filter EZOPEN SDK verbose messages
  React.useEffect(() => {
    const originalConsoleError = console.error;
    const originalConsoleLog = console.log;
    
    console.error = (...args: any[]) => {
      const errorMsg = args.join(' ');
      // Suppress all EZOPEN-related errors from console
      if (errorMsg.includes('wasmplayer') || 
          errorMsg.includes('jsPlugin') || 
          errorMsg.includes('ezopen') ||
          errorMsg.includes('EZOPEN') ||
          errorMsg.includes("reading 'width'") ||
          errorMsg.includes("reading 'JS_Stop'") ||
          errorMsg.includes("reading 'JS_SetSecretKey'") ||
          errorMsg.includes('Cannot read properties of null') ||
          errorMsg.includes('Unknown promise rejection') ||
          errorMsg.includes('Failed to play live stream') ||
          errorMsg.includes('Failed to set secret key') ||
          errorMsg.includes('Failed to set verification code') ||
          errorMsg.includes('Auto-play failed') ||
          errorMsg.includes('Plugin initialization failed') ||
          errorMsg.includes('Retry failed')) {
        // Suppress all EZOPEN errors from console
        return;
      }
      originalConsoleError.apply(console, args);
    };
    
    console.log = (...args: any[]) => {
      const logMsg = args.join(' ');
      // Suppress verbose EZOPEN plugin internal logging
      if (logMsg.includes('[iWndNum-') || 
          logMsg.includes('mediaElement pause state') ||
          logMsg.includes('wasmplayer') ||
          logMsg.includes('jsPlugin internal')) {
        // Suppress verbose plugin logs that flood the console
        return;
      }
      originalConsoleLog.apply(console, args);
    };
    
    return () => {
      console.error = originalConsoleError;
      console.log = originalConsoleLog;
    };
  }, []);
  
  // Global error suppression for EZOPEN SDK
  React.useEffect(() => {
    // Override the error reporting function React uses for the error overlay
    const originalWindowError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      const errorMsg = String(message || '');
      const errorStack = error?.stack || '';
      const errorSource = String(source || '');
      
      // Suppress EZOPEN SDK errors completely
      if (errorMsg.includes('wasmplayer') || 
          errorMsg.includes('jsPlugin') || 
          errorMsg.includes("reading 'width'") ||
          errorMsg.includes('Cannot read properties of null') ||
          errorMsg.includes('Unknown promise rejection') ||
          errorStack.includes('wasmplayer') ||
          errorStack.includes('jsPlugin') ||
          errorStack.includes('ezopen') ||
          errorSource.includes('wasmplayer') ||
          errorSource.includes('jsPlugin')) {
        return true; // Suppress and prevent default handling
      }
      
      if (originalWindowError) {
        return originalWindowError(message, source, lineno, colno, error);
      }
      return false;
    };
    
    // Suppress EZOPEN SDK errors that can occur after component unmounts
    const suppressEZOPENErrors = (event: ErrorEvent) => {
      const errorMsg = event.message || event.error?.message || '';
      const errorStack = event.error?.stack || '';
      const errorFilename = event.filename || '';
      
      if (errorMsg.includes('wasmplayer') || 
          errorMsg.includes('jsPlugin') || 
          errorMsg.includes("reading 'width'") ||
          errorMsg.includes('Cannot read properties of null') ||
          errorMsg.includes('Unknown promise rejection') ||
          errorStack.includes('wasmplayer') ||
          errorStack.includes('jsPlugin') ||
          errorStack.includes('ezopen') ||
          errorFilename.includes('wasmplayer') ||
          errorFilename.includes('jsPlugin')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
      return true;
    };
    
    const suppressEZOPENRejections = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      
      // Handle undefined or null reasons (EZOPEN SDK throws these)
      if (reason === undefined || reason === null) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }
      
      // Check if reason indicates EZOPEN SDK error
      if (reason) {
        const reasonStr = typeof reason === 'string' ? reason : 
                         reason instanceof Error ? reason.message : 
                         String(reason);
        const reasonStack = reason instanceof Error ? reason.stack || '' : '';
        
        if (reasonStr.includes('Cannot read properties of null') || 
            reasonStr.includes("reading 'width'") ||
            reasonStr.includes('wasmplayer') ||
            reasonStr.includes('jsPlugin') ||
            reasonStr.includes('ezopen') ||
            reasonStr.includes('Unknown') ||
            reasonStack.includes('wasmplayer') ||
            reasonStack.includes('jsPlugin') ||
            reasonStack.includes('ezopen')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }
      }
    };
    
    window.addEventListener('error', suppressEZOPENErrors, true);
    window.addEventListener('unhandledrejection', suppressEZOPENRejections);
    
    return () => {
      window.onerror = originalWindowError;
      window.removeEventListener('error', suppressEZOPENErrors, true);
      window.removeEventListener('unhandledrejection', suppressEZOPENRejections);
    };
  }, []);
  
  return (
    <BrowserRouter>
      <BrandingProvider>
        <PermissionsProvider>
          <AuthProvider>
          <ImpersonationProvider>
            <NotificationProvider>
              <LocationProvider>
              <Routes>
                <Route path="/education" element={<PermissionProtectedRoute screenCode="PERSONS"><EducationDemoPage /></PermissionProtectedRoute>} />
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          <Route path="/platform" element={<ProtectedRoute><PlatformAdminPage /></ProtectedRoute>} />
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <PermissionProtectedRoute screenCode="DASHBOARD">
                <DashboardPage />
              </PermissionProtectedRoute>
            }
          />

          {/* Locations */}
          <Route
            path="/locations"
            element={
              <PermissionProtectedRoute screenCode="LOCATIONS">
                <LocationsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/locations/create"
            element={
              <PermissionProtectedRoute screenCode="LOCATIONS">
                <CreateLocationPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/locations/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="LOCATIONS">
                <EditLocationPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/locations/:id/view"
            element={
              <PermissionProtectedRoute screenCode="LOCATIONS">
                <ViewLocationPage />
              </PermissionProtectedRoute>
            }
          />
          {/* Cameras */}
          <Route
            path="/cameras"
            element={
              <PermissionProtectedRoute screenCode="CAMERAS">
                <CamerasPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/cameras/:id/view"
            element={
              <PermissionProtectedRoute screenCode="CAMERAS">
                <ViewCameraPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/cameras/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="CAMERAS">
                <EditCameraPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/live"
            element={
              <PermissionProtectedRoute screenCode="CAM_LIVE">
                <LiveViewPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/events"
            element={
              <PermissionProtectedRoute screenCode="EVENTS">
                <EventsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/events/:id/view"
            element={
              <PermissionProtectedRoute screenCode="EVENTS">
                <ViewEventPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/events/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="EVENTS">
                <EditEventPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/ai-search"
            element={
              <PermissionProtectedRoute screenCode="AI_SEARCH">
                <AISearchPage />
              </PermissionProtectedRoute>
            }
          />
          <Route path="/sensors-devices" element={<PermissionProtectedRoute screenCode="SENSORS_DEVICES"><SensorsDevicesPage /></PermissionProtectedRoute>} />
          <Route
            path="/actions"
            element={
              <PermissionProtectedRoute screenCode="ACTIONS">
                <ActionsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/actions/:id/view"
            element={
              <PermissionProtectedRoute screenCode="ACTIONS">
                <ViewActionPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/actions/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="ACTIONS">
                <EditActionPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/visitor-logs"
            element={
              <PermissionProtectedRoute screenCode="ENTRY_EXIT">
                <VisitorLogsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/entry-exit"
            element={
              <PermissionProtectedRoute screenCode="ENTRY_EXIT">
                <VisitorLogsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/entry-exit/new"
            element={
              <PermissionProtectedRoute screenCode="ENTRY_EXIT">
                <CreateEntryExitPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/entry-exit/:id"
            element={
              <PermissionProtectedRoute screenCode="ENTRY_EXIT">
                <ViewEntryExitPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/entry-exit/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="ENTRY_EXIT">
                <EditEntryExitPage />
              </PermissionProtectedRoute>
            }
          />

          {/* Parking Management */}
          <Route
            path="/parking"
            element={
              <PermissionProtectedRoute screenCode="PARKING">
                <ParkingLotsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/parking/:lotId/spots"
            element={
              <PermissionProtectedRoute screenCode="PARKING">
                <ParkingSpotsPage />
              </PermissionProtectedRoute>
            }
          />

          {/* Tenant Management */}
          <Route
            path="/tenants"
            element={
              <PermissionProtectedRoute screenCode="TENANTS">
                <TenantsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/tenants/create"
            element={
              <PermissionProtectedRoute screenCode="TENANTS">
                <CreateTenantPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/tenants/:id/view"
            element={
              <PermissionProtectedRoute screenCode="TENANTS">
                <ViewTenantPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/tenants/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="TENANTS">
                <EditTenantPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/company"
            element={
              <PermissionProtectedRoute screenCode="COMPANY">
                <CompanyProfilePage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/company/edit"
            element={
              <PermissionProtectedRoute screenCode="COMPANY">
                <CompanyEditPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <ProtectedRoute>
                <PrivacyPolicyPage />
              </ProtectedRoute>
            }
          />

          {/* Person Registry */}
          <Route
            path="/persons"
            element={
              <PermissionProtectedRoute screenCode="PERSONS">
                <PersonsPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/persons/create"
            element={
              <PermissionProtectedRoute screenCode="PERSONS">
                <CreateEditPersonPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/persons/:id/view"
            element={
              <PermissionProtectedRoute screenCode="PERSONS">
                <ViewPersonPage />
              </PermissionProtectedRoute>
            }
          />
          <Route
            path="/persons/:id/edit"
            element={
              <PermissionProtectedRoute screenCode="PERSONS">
                <CreateEditPersonPage />
              </PermissionProtectedRoute>
            }
          />

          {/* Profile */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Configuration */}
          <Route
            path="/configuration"
            element={
              <ProtectedRoute>
                <ConfigurationPage />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 Page - Must be last */}
          <Route path="*" element={<NotFoundPage />} />
            </Routes>
              </LocationProvider>
            </NotificationProvider>
          </ImpersonationProvider>
          </AuthProvider>
        </PermissionsProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
